<template>
  <div class="min-h-screen" :key="pageKey">
    <Header />

    <!-- Case Study Hero -->
    <section class="relative pt-32 pb-20 overflow-hidden">
      <div class="absolute inset-0 bg-pattern"></div>
      <div class="absolute top-0 right-1/4 w-96 h-96 bg-primary-10 rounded-full blur-3xl"></div>

      <div class="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="max-w-4xl mx-auto text-center">
          <div class="mb-6">
            <span class="inline-block px-4 py-2 bg-primary-10 text-primary rounded-full text-sm font-medium">
              {{ caseStudy.industry }}
            </span>
          </div>

          <h1 class="text-4xl md:text-6xl font-bold mb-6 gradient-text">{{ caseStudy.title }}</h1>
          <p class="text-xl text-gray-800 dark:text-dark-muted mb-8">{{ caseStudy.description }}</p>

          <div v-if="caseStudy.metrics && caseStudy.metrics.length >= 2" class="flex flex-wrap justify-center gap-8 text-center">
            <div>
              <div class="text-3xl font-bold gradient-text">{{ caseStudy.metrics[0]?.value }}</div>
              <div class="text-sm text-gray-800 dark:text-dark-muted">{{ caseStudy.metrics[0]?.label }}</div>
            </div>
            <div>
              <div class="text-3xl font-bold gradient-text">{{ caseStudy.metrics[1]?.value }}</div>
              <div class="text-sm text-gray-800 dark:text-dark-muted">{{ caseStudy.metrics[1]?.label }}</div>
            </div>
            <div>
              <div class="text-3xl font-bold gradient-text">{{ caseStudy.timeline }}</div>
              <div class="text-sm text-gray-800 dark:text-dark-muted">项目周期</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Case Study Content -->
    <section class="py-20">
      <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <!-- Challenge -->
        <div v-if="caseStudy.challenges && caseStudy.challenges.length > 0" class="mb-16">
          <h2 class="text-3xl font-bold mb-6 text-dark-text">面临的挑战</h2>
          <div class="space-y-4">
            <div v-for="(challenge, index) in caseStudy.challenges" :key="index"
                 class="glass-card p-6 flex items-start">
              <svg class="w-6 h-6 text-red-500 mr-4 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"></path>
              </svg>
              <div>
                <h3 class="text-lg font-semibold mb-2 text-dark-text">{{ challenge.title }}</h3>
                <p class="text-gray-800 dark:text-dark-muted">{{ challenge.description }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Solution -->
        <div v-if="caseStudy.solutions && caseStudy.solutions.length > 0" class="mb-16">
          <h2 class="text-3xl font-bold mb-6 text-dark-text">我们的解决方案</h2>
          <div class="space-y-6">
            <div v-for="(solution, index) in caseStudy.solutions" :key="index"
                 class="glass-card p-6">
              <div class="flex items-start">
                <svg class="w-6 h-6 text-green-500 mr-4 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
                </svg>
                <div>
                  <h3 class="text-lg font-semibold mb-2 text-dark-text">{{ solution.title }}</h3>
                  <p class="text-gray-800 dark:text-dark-muted mb-4">{{ solution.description }}</p>
                  <div class="flex flex-wrap gap-2">
                    <span v-for="tag in solution.tags" :key="tag"
                          class="px-3 py-1 bg-primary-10 border border-primary-20 rounded-full text-xs text-primary">
                      {{ tag }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Implementation Process -->
        <div v-if="caseStudy.implementation && caseStudy.implementation.length > 0" class="mb-16">
          <h2 class="text-3xl font-bold mb-6 text-dark-text">实施过程</h2>
          <div class="relative">
            <div class="absolute left-4 top-0 bottom-0 w-0.5 bg-primary-30"></div>
            <div class="space-y-8">
              <div v-for="(step, index) in caseStudy.implementation" :key="index"
                   class="relative pl-12">
                <div class="absolute left-0 w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                  <span class="text-white text-sm font-bold">{{ index + 1 }}</span>
                </div>
                <div class="glass-card p-6">
                  <h3 class="text-lg font-semibold mb-2 text-dark-text">{{ step.phase }}</h3>
                  <p class="text-gray-800 dark:text-dark-muted text-sm mb-2">{{ step.duration }}</p>
                  <p class="text-gray-800 dark:text-dark-muted">{{ step.description }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Results -->
        <div v-if="caseStudy.results && caseStudy.results.length > 0" class="mb-16">
          <h2 class="text-3xl font-bold mb-6 text-dark-text">项目成果</h2>
          <div class="grid md:grid-cols-2 gap-6">
            <div v-for="(result, index) in caseStudy.results" :key="index"
                 class="glass-card p-6">
              <div class="flex justify-between items-center mb-3">
                <span class="text-gray-800 dark:text-dark-text font-medium">{{ result.label }}</span>
                <span class="text-2xl font-bold gradient-text">{{ result.value }}</span>
              </div>
              <div class="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2 transition-colors duration-200">
                <div class="bg-gradient-primary h-2 rounded-full transition-all duration-1000"
                     :style="{ width: result.percentage }"></div>
              </div>
              <p class="text-gray-800 dark:text-dark-text text-sm mt-2">{{ result.description }}</p>
            </div>
          </div>
        </div>

        <!-- Testimonial -->
        <div v-if="caseStudy.testimonial && caseStudy.testimonial.quote" class="mb-16">
          <div class="glass-card p-8 border-l-4 border-primary">
            <div class="flex items-start">
              <div class="mr-4">
                <FeatureIcon name="chat" size="xl" variant="gradient" />
              </div>
              <div>
                <p class="text-gray-700 dark:text-dark-text text-lg italic mb-4">{{ caseStudy.testimonial.quote }}</p>
                <div class="flex items-center">
                  <div class="w-12 h-12 rounded-full icon-shell icon-shell-strong flex items-center justify-center mr-4">
                    <FeatureIcon name="user" size="lg" variant="minimal" />
                  </div>
                  <div>
                    <div class="font-semibold text-gray-700 dark:text-dark-text">{{ caseStudy.testimonial.author }}</div>
                    <div class="text-gray-800 dark:text-dark-muted text-sm">{{ caseStudy.testimonial.position }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 详细内容部分 -->
    <section class="py-20">
      <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 class="text-3xl font-bold mb-8 gradient-text">详细案例分析</h2>

        <div v-if="caseStudy.detailedContent" v-html="caseStudy.detailedContent" class="case-detail-content prose prose-lg max-w-none"></div>
        <div v-else class="p-8 text-center text-gray-500 dark:text-dark-muted">
          暂无详细内容
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="py-32 relative">
      <div class="absolute inset-0 bg-gradient-to-r from-primary-5 via-accent-5 to-primary-5"></div>

      <div class="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 class="text-4xl font-bold mb-6 gradient-text">想获得类似的成功案例吗？</h2>
        <p class="text-xl text-gray-800 dark:text-dark-muted mb-12">
          让我们帮助您的企业实现数字化增长，创造属于您的成功故事
        </p>

        <div class="flex flex-col sm:flex-row gap-6 justify-center">
          <button @click="contactUs" class="btn-primary">
            开始合作
          </button>
          <NuxtLink to="/cases" class="btn-secondary inline-flex items-center justify-center">
            查看更多案例
            <svg class="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
            </svg>
          </NuxtLink>
        </div>
      </div>
    </section>

    <Footer />
  </div>
</template>

<script setup lang="ts">
const route = useRoute()

const id = computed(() => {
  return route.params.id || '1'
})

// 添加 key 属性用于强制刷新
const pageKey = computed(() => `case-${id.value}`)

// 示例案例数据
const caseStudies: Record<string, any> = {
  '1': {
    title: '某知名电商平台',
    industry: '电商行业',
    description: '该平台面临用户获取成本高、转化率低的问题。通过我们的DSP+DMP整合方案，实现了精准用户定位和智能投放优化。',
    icon: '🛒',
    metrics: [
      { value: '+180%', label: '转化率提升' },
      { value: '-45%', label: '获客成本' }
    ],
    timeline: '3个月',
    challenges: [
      { title: '获客成本高企', description: '流量成本逐年上升，传统投放方式ROI低下，难以获得高质量用户。' },
      { title: '转化率低', description: '网站流量质量参差不齐，用户行为路径不清晰，导致最终转化率偏低。' },
      { title: '数据孤岛', description: '各渠道数据分散，缺乏统一的用户画像和行为分析体系。' }
    ],
    solutions: [
      {
        title: 'DMP用户画像系统',
        description: '构建了完整的用户画像体系，整合多源数据，实现精准用户分层和定向投放。',
        tags: ['用户画像', '数据整合', '精准定向']
      },
      {
        title: 'DSP智能投放',
        description: '基于AI算法的智能投放系统，自动优化投放策略，提升广告效果。',
        tags: ['程序化投放', 'AI优化', '实时竞价']
      },
      {
        title: '全链路数据分析',
        description: '从曝光到转化的全链路数据监控，及时发现并优化投放薄弱环节。',
        tags: ['数据分析', '转化优化', '实时监控']
      }
    ],
    implementation: [
      { phase: '需求分析与数据整合', duration: '第1个月', description: '深入了解业务需求，整合现有数据源，搭建基础数据架构。' },
      { phase: '用户画像构建', duration: '第2个月', description: '基于历史数据构建用户画像模型，开发DMP系统核心功能。' },
      { phase: '智能投放优化', duration: '第3个月', description: '部署DSP系统，进行A/B测试和算法优化，持续提升投放效果。' }
    ],
    results: [
      { label: '转化率提升', value: '+180%', percentage: '90%', description: '相比投放前的转化率提升180%' },
      { label: '获客成本降低', value: '-45%', percentage: '55%', description: '单用户获取成本降低45%' },
      { label: 'ROI提升', value: '+320%', percentage: '95%', description: '广告投资回报率提升320%' },
      { label: '用户质量', value: '+80%', percentage: '80%', description: '高质量用户占比提升80%' }
    ],
    testimonial: {
      quote: '"MAGIC的专业团队和先进技术帮助我们实现了业务突破性增长，转化率提升了180%，ROI提升了320%！这是我们合作过的最优秀的数字营销团队。"',
      author: '张总',
      position: '市场总监'
    },
    detailedContent: `
      <div class="space-y-8">
        <div class="glass-card p-8 border-l-4 border-primary">
          <h3 class="text-2xl font-bold mb-4 text-dark-text">项目背景</h3>
          <p class="text-gray-800 dark:text-dark-muted leading-relaxed mb-4">
            该电商平台是国内领先的B2C电商平台，月活跃用户超过5000万。随着市场竞争加剧，平台面临着严峻的挑战：
            用户获取成本持续上升，从2019年的15元/人增长到2022年的45元/人；转化率却呈现下降趋势，从3.2%降至1.8%。
          </p>
          <div class="grid md:grid-cols-3 gap-4 mt-6">
            <div class="bg-primary-50 dark:bg-white/5 rounded-lg p-4 text-center">
              <div class="text-3xl font-bold text-primary mb-2">45元</div>
              <div class="text-sm text-gray-800 dark:text-dark-muted">获客成本</div>
            </div>
            <div class="bg-accent-50 dark:bg-white/5 rounded-lg p-4 text-center">
              <div class="text-3xl font-bold text-accent mb-2">1.8%</div>
              <div class="text-sm text-gray-800 dark:text-dark-muted">转化率</div>
            </div>
            <div class="bg-stellar-100 dark:bg-white/5 rounded-lg p-4 text-center">
              <div class="text-3xl font-bold text-stellar mb-2">-67%</div>
              <div class="text-sm text-gray-800 dark:text-dark-muted">ROI下降</div>
            </div>
          </div>
        </div>

        <div>
          <h3 class="text-2xl font-bold mb-6 text-dark-text">解决方案详情</h3>
          <div class="grid md:grid-cols-2 gap-6">
            <div class="glass-card p-6">
              <div class="w-16 h-16 mb-4 rounded-2xl bg-gradient-to-br from-cosmic/20 to-nebula/20 flex items-center justify-center">
                <svg class="w-8 h-8 text-cosmic" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"/>
                  <circle cx="12" cy="12" r="3"/>
                  <path d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83"/>
                </svg>
              </div>
              <h4 class="text-xl font-bold mb-3 text-dark-text">精准用户画像</h4>
              <p class="text-gray-800 dark:text-dark-muted mb-4">
                整合了平台内外的12个数据源，构建了包含300+标签的用户画像体系。
                通过机器学习算法，识别出高价值用户的18个关键特征。
              </p>
              <div class="flex flex-wrap gap-2">
                <span class="px-3 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary rounded-full text-xs">购买意向</span>
                <span class="px-3 py-1 bg-accent-100 dark:bg-accent-900/30 text-accent rounded-full text-xs">价格敏感度</span>
                <span class="px-3 py-1 bg-stellar-100 dark:bg-stellar-900/30 text-stellar rounded-full text-xs">品牌偏好</span>
              </div>
            </div>

            <div class="glass-card p-6">
              <div class="w-16 h-16 mb-4 rounded-2xl bg-gradient-to-br from-cosmic/20 to-nebula/20 flex items-center justify-center">
                <svg class="w-8 h-8 text-nebula" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7h1a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-1H2a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h1a7 7 0 0 1 7-7H9V5.73c-.6-.34-1-.99-1-1.73a2 2 0 0 1 2-2z"/>
                  <circle cx="9" cy="12" r="1"/>
                  <circle cx="15" cy="12" r="1"/>
                </svg>
              </div>
              <h4 class="text-xl font-bold mb-3 text-dark-text">AI智能投放</h4>
              <p class="text-gray-800 dark:text-dark-muted mb-4">
                基于深度学习的实时竞价系统，每秒处理10万+竞价请求。
                动态调整出价策略，确保每次曝光都能获得最大收益。
              </p>
              <div class="bg-gray-100 dark:bg-white/5 rounded-lg p-3">
                <div class="text-sm text-gray-800 dark:text-dark-muted mb-1">响应时间</div>
                <div class="text-2xl font-bold text-primary">&lt;50ms</div>
              </div>
            </div>
          </div>
        </div>

        <div class="glass-card p-8">
          <h3 class="text-2xl font-bold mb-6 text-dark-text">实施效果对比</h3>
          <div class="grid md:grid-cols-2 gap-8">
            <div>
              <h4 class="text-lg font-semibold mb-4 text-red-500">优化前</h4>
              <div class="space-y-3">
                <div class="flex justify-between items-center p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                  <span class="text-gray-800 dark:text-dark-muted">获客成本</span>
                  <span class="font-bold text-red-500">45元</span>
                </div>
                <div class="flex justify-between items-center p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                  <span class="text-gray-800 dark:text-dark-muted">转化率</span>
                  <span class="font-bold text-red-500">1.8%</span>
                </div>
                <div class="flex justify-between items-center p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                  <span class="text-gray-800 dark:text-dark-muted">ROI</span>
                  <span class="font-bold text-red-500">1:2.5</span>
                </div>
              </div>
            </div>
            <div>
              <h4 class="text-lg font-semibold mb-4 text-green-500">优化后</h4>
              <div class="space-y-3">
                <div class="flex justify-between items-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <span class="text-gray-800 dark:text-dark-muted">获客成本</span>
                  <span class="font-bold text-green-500">25元</span>
                </div>
                <div class="flex justify-between items-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <span class="text-gray-800 dark:text-dark-muted">转化率</span>
                  <span class="font-bold text-green-500">5.0%</span>
                </div>
                <div class="flex justify-between items-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <span class="text-gray-800 dark:text-dark-muted">ROI</span>
                  <span class="font-bold text-green-500">1:8.3</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `
  },
  '2': {
    title: '某游戏公司',
    industry: '游戏行业',
    description: '通过DSP精准投放，用户获取成本降低50%，日活用户增长200%，游戏收入实现翻番。',
    icon: '🎮',
    metrics: [
      { value: '-50%', label: '获客成本' },
      { value: '+200%', label: '日活增长' }
    ],
    timeline: '2个月',
    challenges: [
      { title: '用户质量参差不齐', description: '传统投放渠道用户质量不稳定，付费率和留存率低下。' },
      { title: '变现效率低', description: '广告位配置不合理，eCPM偏低，影响整体收益。' }
    ],
    solutions: [
      {
        title: '游戏用户定向',
        description: '基于游戏行为特征的用户定向系统，精准触达高价值游戏玩家。',
        tags: ['用户定向', '行为分析', '游戏画像']
      },
      {
        title: 'SSP收益优化',
        description: '优化广告位配置和竞价策略，显著提升广告收益和用户体验。',
        tags: ['收益优化', '广告位配置', '实时竞价']
      }
    ],
    implementation: [
      { phase: '用户分析', duration: '第1个月前半月', description: '分析游戏用户行为特征，构建玩家画像模型。' },
      { phase: '投放优化', duration: '第1个月后半月', description: '基于画像进行精准投放，优化投放策略和素材。' },
      { phase: '变现提升', duration: '第2个月', description: '优化广告位配置，提升广告填充率和eCPM。' }
    ],
    results: [
      { label: '获客成本降低', value: '-50%', percentage: '50%', description: '单用户获取成本降低50%' },
      { label: '日活增长', value: '+200%', percentage: '100%', description: '日活跃用户增长200%' },
      { label: '付费率提升', value: '+120%', percentage: '70%', description: '用户付费率提升120%' },
      { label: 'ARPU提升', value: '+80%', percentage: '80%', description: '每用户平均收入提升80%' }
    ],
    testimonial: {
      quote: '"通过MAGIC的解决方案，我们的游戏用户质量和收益都得到了显著提升。ROI提升了150%，用户留存率也大幅改善。强烈推荐！"',
      author: '李总',
      position: 'CEO'
    },
    detailedContent: `
      <div class="space-y-8">
        <div class="glass-card p-8 border-l-4 border-accent">
          <h3 class="text-2xl font-bold mb-4 text-dark-text">游戏行业获客难题</h3>
          <p class="text-gray-800 dark:text-dark-muted leading-relaxed mb-4">
            这款RPG手游在上线初期面临着严重的获客难题：广告投放成本高昂但用户质量低下，
            大量低质量用户导致服务器压力增加但收入增长有限。付费率仅为0.8%，远低于行业平均水平。
          </p>
        </div>

        <div>
          <h3 class="text-2xl font-bold mb-6 text-dark-text">游戏玩家画像系统</h3>
          <div class="glass-card p-6 mb-6">
            <div class="grid md:grid-cols-3 gap-6">
              <div class="text-center">
                <div class="w-16 h-16 mx-auto mb-3 rounded-2xl bg-gradient-to-br from-cosmic/20 to-nebula/20 flex items-center justify-center">
                  <svg class="w-8 h-8 text-cosmic" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <rect x="2" y="6" width="20" height="12" rx="2"/>
                    <path d="M6 12h4m-2-2v4m6-2h4"/>
                  </svg>
                </div>
                <h4 class="font-bold mb-2 text-dark-text">游戏偏好</h4>
                <p class="text-sm text-gray-800 dark:text-dark-muted">RPG/SLG/休闲游戏分类</p>
              </div>
              <div class="text-center">
                <div class="w-16 h-16 mx-auto mb-3 rounded-2xl bg-gradient-to-br from-stellar/20 to-cosmic/20 flex items-center justify-center">
                  <svg class="w-8 h-8 text-stellar" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8"/>
                    <path d="M12 18V6"/>
                  </svg>
                </div>
                <h4 class="font-bold mb-2 text-dark-text">付费意愿</h4>
                <p class="text-sm text-gray-800 dark:text-dark-muted">大R/中R/小R用户分层</p>
              </div>
              <div class="text-center">
                <div class="w-16 h-16 mx-auto mb-3 rounded-2xl bg-gradient-to-br from-nebula/20 to-stellar/20 flex items-center justify-center">
                  <svg class="w-8 h-8 text-nebula" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="10"/>
                    <polyline points="12 6 12 12 16 14"/>
                  </svg>
                </div>
                <h4 class="font-bold mb-2 text-dark-text">活跃时段</h4>
                <p class="text-sm text-gray-800 dark:text-dark-muted">24小时行为模式分析</p>
              </div>
            </div>
          </div>
        </div>

        <div class="glass-card p-8">
          <h3 class="text-2xl font-bold mb-6 text-dark-text">收益增长数据</h3>
          <div class="grid md:grid-cols-2 gap-6">
            <div class="bg-gradient-to-br from-primary-50 to-accent-50 dark:from-white/5 dark:to-white/10 rounded-lg p-6">
              <div class="text-4xl font-bold gradient-text mb-2">+200%</div>
              <div class="text-gray-800 dark:text-dark-muted">日活用户增长</div>
              <div class="mt-4 text-sm text-gray-600 dark:text-dark-muted">
                从5万DAU增长到15万DAU
              </div>
            </div>
            <div class="bg-gradient-to-br from-accent-50 to-stellar-50 dark:from-white/5 dark:to-white/10 rounded-lg p-6">
              <div class="text-4xl font-bold gradient-text mb-2">0.8% → 2.5%</div>
              <div class="text-gray-800 dark:text-dark-muted">付费率提升</div>
              <div class="mt-4 text-sm text-gray-600 dark:text-dark-muted">
                接近行业顶级水平
              </div>
            </div>
          </div>
        </div>
      </div>
    `
  },
  '3': {
    title: '某在线教育平台',
    industry: '教育培训',
    description: '私域流量运营方案，学员留存率提升180%，复购率提升120%。',
    icon: '📚',
    metrics: [
      { value: '+180%', label: '留存率' },
      { value: '+120%', label: '复购率' }
    ],
    timeline: '4个月',
    challenges: [
      { title: '获客成本高昂', description: '教育培训行业竞争激烈，单个学员获取成本超过800元。' },
      { title: '用户生命周期短', description: '学员完课率低，复购意愿不强，LTV偏低。' }
    ],
    solutions: [
      { title: '私域流量池构建', description: '搭建微信生态私域流量池，实现用户精细化运营。', tags: ['私域运营', '用户留存', '社群管理'] },
      { title: '智能推荐系统', description: '基于学习行为的课程推荐，提升完课率和满意度。', tags: ['AI推荐', '个性化学习', '数据分析'] }
    ],
    implementation: [
      { phase: '私域搭建', duration: '第1个月', description: '搭建企业微信、社群、小程序矩阵。' },
      { phase: '运营策略', duration: '第2-3个月', description: '设计用户分层运营策略和内容体系。' },
      { phase: '效果优化', duration: '第4个月', description: '数据驱动优化，提升转化效果。' }
    ],
    results: [
      { label: '留存率提升', value: '+180%', percentage: '85%', description: '学员完课留存率提升180%' },
      { label: '复购率提升', value: '+120%', percentage: '75%', description: '课程复购率提升120%' },
      { label: 'LTV提升', value: '+200%', percentage: '90%', description: '用户生命周期价值提升200%' },
      { label: '获客成本降低', value: '-60%', percentage: '40%', description: '单用户获取成本降低60%' }
    ],
    testimonial: {
      quote: '"私域运营帮助我们建立了稳定的用户增长飞轮。现在我们的学员留存率和复购率都远超行业平均水平！"',
      author: '王总',
      position: '运营总监'
    },
    detailedContent: `
      <div class="space-y-8">
        <div class="glass-card p-8 border-l-4 border-stellar">
          <h3 class="text-2xl font-bold mb-4 text-dark-text">在线教育获客困局</h3>
          <p class="text-gray-800 dark:text-dark-muted leading-relaxed mb-4">
            该在线教育平台专注于K12英语培训，面临着获客成本高昂和用户生命周期短的双重挑战。
            传统渠道获客成本超过800元/人，但学员平均生命周期只有3个月，严重影响了盈利能力。
          </p>
        </div>

        <div>
          <h3 class="text-2xl font-bold mb-6 text-dark-text">私域运营架构</h3>
          <div class="grid md:grid-cols-3 gap-6">
            <div class="glass-card p-6 flex flex-col">
              <div class="w-16 h-16 mb-4 rounded-2xl bg-gradient-to-br from-stellar/20 to-cosmic/20 flex items-center justify-center">
                <svg class="w-8 h-8 text-stellar" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
              </div>
              <h4 class="text-xl font-bold mb-3 text-dark-text">社群矩阵</h4>
              <p class="text-gray-800 dark:text-dark-muted mb-4 flex-grow">
                搭建了200+细分社群，按年级、科目、学习进度进行精准分层，
                实现精细化管理。
              </p>
              <div class="bg-stellar-50 dark:bg-white/5 rounded-lg p-3 text-center">
                <div class="text-2xl font-bold text-stellar">50万+</div>
                <div class="text-sm text-gray-800 dark:text-dark-muted">私域用户</div>
              </div>
            </div>

            <div class="glass-card p-6 flex flex-col">
              <div class="w-16 h-16 mb-4 rounded-2xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
                <svg class="w-8 h-8 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="5" y="2" width="14" height="20" rx="2"/>
                  <path d="M12 18h.01"/>
                </svg>
              </div>
              <h4 class="text-xl font-bold mb-3 text-dark-text">小程序生态</h4>
              <p class="text-gray-800 dark:text-dark-muted mb-4 flex-grow">
                开发学习打卡、作业提交、进度追踪等功能小程序，
                提升用户粘性和活跃度。
              </p>
              <div class="bg-primary-50 dark:bg-white/5 rounded-lg p-3 text-center">
                <div class="text-2xl font-bold text-primary">85%</div>
                <div class="text-sm text-gray-800 dark:text-dark-muted">日活跃率</div>
              </div>
            </div>

            <div class="glass-card p-6 flex flex-col">
              <div class="w-16 h-16 mb-4 rounded-2xl bg-gradient-to-br from-nebula/20 to-stellar/20 flex items-center justify-center">
                <svg class="w-8 h-8 text-nebula" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7h1a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-1H2a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h1a7 7 0 0 1 7-7H9V5.73c-.6-.34-1-.99-1-1.73a2 2 0 0 1 2-2z"/>
                  <circle cx="9" cy="12" r="1"/>
                  <circle cx="15" cy="12" r="1"/>
                </svg>
              </div>
              <h4 class="text-xl font-bold mb-3 text-dark-text">AI助教</h4>
              <p class="text-gray-800 dark:text-dark-muted mb-4 flex-grow">
                智能推荐个性化学习内容，24小时答疑，
                大幅提升学习体验和完课率。
              </p>
              <div class="bg-accent-50 dark:bg-white/5 rounded-lg p-3 text-center">
                <div class="text-2xl font-bold text-accent">92%</div>
                <div class="text-sm text-gray-800 dark:text-dark-muted">完课率</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `
  },
  '4': {
    title: '某电商平台',
    industry: '电子商务',
    description: 'SSP+DSP整合方案，广告收益提升80%，用户转化率提升160%。',
    icon: '🛒',
    metrics: [
      { value: '+80%', label: '广告收益' },
      { value: '+160%', label: '转化率' }
    ],
    timeline: '2.5个月',
    challenges: [
      { title: '流量变现效率低', description: '广告位填充率低，eCPM偏低，影响整体收益。' },
      { title: '用户体验差', description: '广告与内容不匹配，影响用户体验和平台口碑。' }
    ],
    solutions: [
      { title: 'SSP媒体变现', description: '接入优质广告源，提升填充率和eCPM。', tags: ['SSP', '媒体变现', '收益优化'] },
      { title: '智能广告匹配', description: '基于用户画像的智能推荐，平衡收益与体验。', tags: ['AI推荐', '用户体验', '精准投放'] }
    ],
    implementation: [
      { phase: 'SSP对接', duration: '第1个月', description: '对接主流广告平台，优化广告位配置。' },
      { phase: '算法优化', duration: '第2个月', description: '训练广告匹配算法，提升精准度。' },
      { phase: '效果调优', duration: '第3个月前半月', description: '持续优化，平衡收益与用户体验。' }
    ],
    results: [
      { label: '广告收益提升', value: '+80%', percentage: '80%', description: '广告总收益提升80%' },
      { label: '填充率提升', value: '+95%', percentage: '95%', description: '广告位填充率提升至95%' },
      { label: 'eCPM提升', value: '+120%', percentage: '80%', description: '千次展示收益提升120%' },
      { label: '用户体验', value: '+40%', percentage: '70%', description: '用户满意度提升40%' }
    ],
    testimonial: {
      quote: '"SSP+DSP的整合方案让我们的流量变现效率大幅提升，同时用户体验也得到了改善。完美的平衡！"',
      author: '刘总',
      position: '技术总监'
    },
    detailedContent: `
      <div class="space-y-8">
        <div class="glass-card p-8">
          <h3 class="text-2xl font-bold mb-6 text-dark-text">电商流量变现优化</h3>
          <p class="text-gray-800 dark:text-dark-muted leading-relaxed mb-6">
            该电商平台拥有千万级月活用户，但广告变现效率一直不理想。
            通过MAGIC的SSP+DSP整合方案，实现了收益与用户体验的双赢。
          </p>

          <div class="grid md:grid-cols-2 gap-6">
            <div class="bg-gradient-to-br from-primary-50 to-primary-100 dark:from-white/5 dark:to-white/10 rounded-lg p-6">
              <h4 class="text-lg font-bold mb-4 text-dark-text">优化前</h4>
              <div class="space-y-3">
                <div class="flex justify-between">
                  <span class="text-gray-800 dark:text-dark-muted">填充率</span>
                  <span class="font-bold text-red-500">45%</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-800 dark:text-dark-muted">eCPM</span>
                  <span class="font-bold text-red-500">¥15</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-800 dark:text-dark-muted">用户投诉</span>
                  <span class="font-bold text-red-500">8%</span>
                </div>
              </div>
            </div>

            <div class="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-900/30 rounded-lg p-6">
              <h4 class="text-lg font-bold mb-4 text-dark-text">优化后</h4>
              <div class="space-y-3">
                <div class="flex justify-between">
                  <span class="text-gray-800 dark:text-dark-muted">填充率</span>
                  <span class="font-bold text-green-500">95%</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-800 dark:text-dark-muted">eCPM</span>
                  <span class="font-bold text-green-500">¥35</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-800 dark:text-dark-muted">用户投诉</span>
                  <span class="font-bold text-green-500">2%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `
  },
  '5': {
    title: '某医疗APP',
    industry: '医疗健康',
    description: '海外ADX跨境投放，国际用户增长300%，品牌知名度大幅提升。',
    icon: '🏥',
    metrics: [
      { value: '+300%', label: '国际用户' },
      { value: '+250%', label: '品牌曝光' }
    ],
    timeline: '3个月',
    challenges: [
      { title: '海外市场不了解', description: '缺乏海外市场经验，不知道如何有效获客。' },
      { title: '合规风险高', description: '各国医疗广告法规复杂，合规成本高。' }
    ],
    solutions: [
      { title: '跨境投放策略', description: '制定针对不同国家和地区的本地化投放策略。', tags: ['跨境营销', '本地化', '海外投放'] },
      { title: '合规保障', description: '专业法务团队审核，确保投放内容符合各国法规。', tags: ['合规审核', '风险控制', '法务支持'] }
    ],
    implementation: [
      { phase: '市场调研', duration: '第1个月', description: '调研目标市场，制定本地化策略。' },
      { phase: '合规审核', duration: '第2个月', description: '完成各国法规审核，调整投放素材。' },
      { phase: '投放优化', duration: '第3个月', description: '大规模投放并持续优化效果。' }
    ],
    results: [
      { label: '国际用户增长', value: '+300%', percentage: '85%', description: '海外用户增长300%' },
      { label: '品牌曝光', value: '+250%', percentage: '75%', description: '海外品牌曝光提升250%' },
      { label: '合规风险', value: '0', percentage: '100%', description: '零合规问题' },
      { label: 'CPA降低', value: '-40%', percentage: '60%', description: '单用户获取成本降低40%' }
    ],
    testimonial: {
      quote: '"MAGIC的海外投放能力让我们在全球市场快速站稳脚跟。专业的合规保障让我们无后顾之忧！"',
      author: '陈总',
      position: '国际业务总监'
    },
    detailedContent: `
      <div class="space-y-8">
        <div class="glass-card p-8 border-l-4 border-primary">
          <h3 class="text-2xl font-bold mb-4 text-dark-text">医疗出海挑战</h3>
          <p class="text-gray-800 dark:text-dark-muted leading-relaxed mb-4">
            该医疗APP希望拓展海外市场，但面临着对当地市场不了解和合规风险高的双重挑战。
            医疗健康领域的广告监管非常严格，稍有不慎就可能面临巨额罚款。
          </p>
        </div>

        <div>
          <h3 class="text-2xl font-bold mb-6 text-dark-text">全球市场布局</h3>
          <div class="grid md:grid-cols-4 gap-4">
            <div class="glass-card p-4 text-center">
              <div class="text-3xl mb-2">🇺🇸</div>
              <h4 class="font-bold mb-1 text-dark-text">北美</h4>
              <p class="text-sm text-gray-800 dark:text-dark-muted">+150%用户增长</p>
            </div>
            <div class="glass-card p-4 text-center">
              <div class="text-3xl mb-2">🇪🇺</div>
              <h4 class="font-bold mb-1 text-dark-text">欧洲</h4>
              <p class="text-sm text-gray-800 dark:text-dark-muted">+200%用户增长</p>
            </div>
            <div class="glass-card p-4 text-center">
              <div class="text-3xl mb-2">🇯🇵</div>
              <h4 class="font-bold mb-1 text-dark-text">日本</h4>
              <p class="text-sm text-gray-800 dark:text-dark-muted">+180%用户增长</p>
            </div>
            <div class="glass-card p-4 text-center">
              <div class="text-3xl mb-2">🇸🇬</div>
              <h4 class="font-bold mb-1 text-dark-text">东南亚</h4>
              <p class="text-sm text-gray-800 dark:text-dark-muted">+250%用户增长</p>
            </div>
          </div>
        </div>
      </div>
    `
  },
  '6': {
    title: '某汽车品牌',
    industry: '汽车行业',
    description: '一站式开户服务，快速启动投放，销售线索增长220%。',
    icon: '🚗',
    metrics: [
      { value: '+220%', label: '销售线索' },
      { value: '-40%', label: '投放成本' }
    ],
    timeline: '1个月',
    challenges: [
      { title: '开户流程复杂', description: '各大平台开户流程繁琐，耗时长达2-4周。' },
      { title: '投放经验不足', description: '内部团队缺乏专业投放经验，效果难以保证。' }
    ],
    solutions: [
      { title: '快速开户服务', description: '专业团队代办，1小时内完成开户流程。', tags: ['快速开户', '专业服务', '效率提升'] },
      { title: '代运营服务', description: '资深投放团队全程代运营，确保投放效果。', tags: ['代运营', '专业投放', '效果保证'] }
    ],
    implementation: [
      { phase: '需求对接', duration: '第1天', description: '了解投放需求和目标受众。' },
      { phase: '快速开户', duration: '第2-3天', description: '完成各平台开户和资质审核。' },
      { phase: '投放启动', duration: '第1周', description: '制定投放策略并启动投放。' }
    ],
    results: [
      { label: '销售线索增长', value: '+220%', percentage: '90%', description: '有效销售线索增长220%' },
      { label: '开户时效', value: '1小时', percentage: '95%', description: '开户流程缩短至1小时' },
      { label: '成本降低', value: '-40%', percentage: '60%', description: '投放成本降低40%' },
      { label: '团队效率', value: '+300%', percentage: '85%', description: '团队效率提升300%' }
    ],
    testimonial: {
      quote: '"MAGIC的一站式服务让我们的营销活动快速启动。专业的代运营团队让我们的投放效果超出预期！"',
      author: '赵总',
      position: '市场总监'
    },
    detailedContent: `
      <div class="space-y-8">
        <div class="glass-card p-8 border-l-4 border-accent">
          <h3 class="text-2xl font-bold mb-4 text-dark-text">汽车营销数字化转型</h3>
          <p class="text-gray-800 dark:text-dark-muted leading-relaxed mb-4">
            该汽车品牌希望快速启动数字营销活动，但面临着开户流程复杂和内部经验不足的问题。
            传统方式开户需要2-4周时间，严重影响了营销时效性。
          </p>
        </div>

        <div>
          <h3 class="text-2xl font-bold mb-6 text-dark-text">服务流程对比</h3>
          <div class="grid md:grid-cols-2 gap-6">
            <div class="glass-card p-6">
              <h4 class="text-lg font-bold mb-4 text-red-500">传统流程</h4>
              <div class="space-y-3">
                <div class="flex items-center">
                  <div class="w-8 h-8 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mr-3">
                    <span class="text-red-500 font-bold">1</span>
                  </div>
                  <div>
                    <div class="font-semibold text-dark-text">准备材料</div>
                    <div class="text-sm text-gray-800 dark:text-dark-muted">3-5天</div>
                  </div>
                </div>
                <div class="flex items-center">
                  <div class="w-8 h-8 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mr-3">
                    <span class="text-red-500 font-bold">2</span>
                  </div>
                  <div>
                    <div class="font-semibold text-dark-text">提交审核</div>
                    <div class="text-sm text-gray-800 dark:text-dark-muted">5-7天</div>
                  </div>
                </div>
                <div class="flex items-center">
                  <div class="w-8 h-8 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mr-3">
                    <span class="text-red-500 font-bold">3</span>
                  </div>
                  <div>
                    <div class="font-semibold text-dark-text">审核反馈</div>
                    <div class="text-sm text-gray-800 dark:text-dark-muted">3-5天</div>
                  </div>
                </div>
                <div class="mt-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg text-center">
                  <div class="text-lg font-bold text-red-500">总计：11-17天</div>
                </div>
              </div>
            </div>

            <div class="glass-card p-6">
              <h4 class="text-lg font-bold mb-4 text-green-500">MAGIC服务</h4>
              <div class="space-y-3">
                <div class="flex items-center">
                  <div class="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mr-3">
                    <span class="text-green-500 font-bold">1</span>
                  </div>
                  <div>
                    <div class="font-semibold text-dark-text">需求对接</div>
                    <div class="text-sm text-gray-800 dark:text-dark-muted">30分钟</div>
                  </div>
                </div>
                <div class="flex items-center">
                  <div class="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mr-3">
                    <span class="text-green-500 font-bold">2</span>
                  </div>
                  <div>
                    <div class="font-semibold text-dark-text">专业代办</div>
                    <div class="text-sm text-gray-800 dark:text-dark-muted">20分钟</div>
                  </div>
                </div>
                <div class="flex items-center">
                  <div class="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mr-3">
                    <span class="text-green-500 font-bold">3</span>
                  </div>
                  <div>
                    <div class="font-semibold text-dark-text">快速开通</div>
                    <div class="text-sm text-gray-800 dark:text-dark-muted">10分钟</div>
                  </div>
                </div>
                <div class="mt-4 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg text-center">
                  <div class="text-lg font-bold text-green-500">总计：1小时</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `
  }
}

const caseStudy = computed(() => {
  const caseId = id.value || '1'
  return caseStudies[caseId] || caseStudies['1'] || {
    title: '案例不存在',
    industry: '未知',
    description: '暂无案例信息',
    icon: '📄',
    metrics: [],
    timeline: '-',
    challenges: [],
    solutions: [],
    implementation: [],
    results: [],
    testimonial: { quote: '', author: '', position: '' },
    detailedContent: '<div class="p-8 text-center">暂无详细内容</div>'
  }
})

const contactUs = () => {
  window.location.href = '/about#contact'
}

useHead({
  title: computed(() => `${caseStudy.value?.title || '案例详情'} - 成功案例`),
  meta: computed(() => [
    { name: 'description', content: caseStudy.value?.description || '成功案例详情' }
  ])
})

// 监听路由变化，确保页面内容正确更新
watch(() => route.params.id, (newId, oldId) => {
  if (newId !== oldId && newId) {
    console.log('Case route changed:', oldId, '->', newId)
    // 路由变化时触发页面滚动到顶部
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
}, { immediate: false })
</script>

<style scoped>
.case-detail-content :deep(h2) {
  @apply text-2xl font-bold mt-8 mb-4 text-dark-text;
}

/* .case-detail-content :deep(h3) {
  @apply text-xl font-bold mt-6 mb-3 text-dark-text;
} */

/* .case-detail-content :deep(h4) {
  @apply text-lg font-semibold mt-4 mb-2 text-dark-text;
} */

.case-detail-content :deep(p) {
  @apply mb-4 text-gray-800 dark:text-dark-muted leading-relaxed;
}

.case-detail-content :deep(div) {
  @apply text-gray-800 dark:text-dark-muted;
}
</style>
